import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Media selection for the TendV2 card: the ONE writer of "which media is
 * selected" and the ONE gate between a selection and the server.
 *
 * History. The media strip shipped (2026-01) as a swipeable scroll-snap
 * carousel, so the scroll offset WAS the user's choice: the card derived the
 * selection from scroll events and saved it on a debounce. Swipe was removed
 * 2026-08 (dots only), but both halves stayed, so from then on the offset was
 * an output that could still write state. Any DOM move (a keyed reorder on
 * the Tended page, a PIP portal swap) resets a moved node's scroll offset to
 * 0 with no event, and the next stray scroll event wrote the FIRST image back
 * as the primary (Buggie Boo 3ec9a8a4…8cd9, 2026-10-01).
 *
 * Now. The strip is a projection of `selectedIndex` (a translated track;
 * nothing scrolls, nothing listens), `selectMedia` is the only setter, and a
 * save happens only when the caller says so: a dot click passes
 * `persist: true`; upload / delete / picker save their own richer payloads
 * and report the URL the server now holds via `markPersisted`. Prop churn,
 * refetches, re-renders and DOM moves cannot reach the server.
 */
export interface UseMediaSelectionArgs {
  itemId: number;
  /** The primary the server holds for this item (single mode anchors to it). */
  primaryUrl: string | null | undefined;
  /** The media list the selection indexes into. */
  images: string[];
  /** Anchor to `primaryUrl` only while true (carousel slides drive their own). */
  anchor: boolean;
  /** Save a `persist: true` selection. Single-mode no-ops are skipped before this runs. */
  onPersist: (url: string, index: number) => void | Promise<void>;
}

export interface SelectMediaOptions {
  /** Save the selection (a user's choice). Default false. */
  persist?: boolean;
  /** The list the index refers to when it is not the current `images` yet (an upload / delete that changes the list in the same tick). */
  images?: string[];
}

export interface MediaSelection {
  selectedIndex: number;
  /** The only way to change the selection. */
  selectMedia: (index: number, opts?: SelectMediaOptions) => void;
  /** A caller saved `url` as the primary itself (upload / delete / picker). */
  markPersisted: (url: string) => void;
}

export function useMediaSelection({ itemId, primaryUrl, images, anchor, onPersist }: UseMediaSelectionArgs): MediaSelection {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const indexRef = useRef(0);
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const onPersistRef = useRef(onPersist);
  onPersistRef.current = onPersist;
  // The primary the server holds: anchored from the prop, or saved by us. A
  // prop that later echoes it is not a change; a prop that differs is new
  // truth to re-anchor to, never a reason to write our own value back.
  const serverPrimaryRef = useRef<string>('');
  const anchoredForItemRef = useRef<number | null>(null);

  // Anchor the selection to the server's primary: once per item, and again
  // only when the primary changes under us or the selection falls out of
  // range. A user's choice (incl. an upload making the new media active)
  // otherwise wins over list churn.
  useEffect(() => {
    if (!anchor || images.length === 0) return;
    const primary = primaryUrl || '';
    const itemChanged = anchoredForItemRef.current !== itemId;
    const primaryChanged = serverPrimaryRef.current !== primary;
    const current = indexRef.current;
    const currentValid = current >= 0 && current < images.length;
    if (!itemChanged && !primaryChanged && currentValid) return;
    const idx = images.indexOf(primary);
    if (!itemChanged && primaryChanged && idx < 0 && currentValid) {
      // The new primary is not in our list yet (the media_urls mirror lands
      // a render later) — keep the selection, let that pass re-anchor.
      return;
    }
    anchoredForItemRef.current = itemId;
    serverPrimaryRef.current = primary;
    const next = idx >= 0 ? idx : 0;
    indexRef.current = next;
    setSelectedIndex(next);
  }, [itemId, primaryUrl, images, anchor]);

  const selectMedia = useCallback((index: number, opts?: SelectMediaOptions) => {
    const list = opts?.images ?? imagesRef.current;
    const clamped = list.length > 0 ? Math.max(0, Math.min(index, list.length - 1)) : 0;
    indexRef.current = clamped;
    setSelectedIndex(clamped);
    if (!opts?.persist) return;
    const url = list[clamped];
    if (!url) return;
    if (anchorRef.current) {
      if (url === serverPrimaryRef.current) return; // the server already has it
      serverPrimaryRef.current = url;
    }
    void onPersistRef.current(url, clamped);
  }, []);

  const markPersisted = useCallback((url: string) => {
    serverPrimaryRef.current = url;
  }, []);

  return { selectedIndex, selectMedia, markPersisted };
}

export default useMediaSelection;
